import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Shield, Clock, DollarSign, Lock, ChevronDown, ChevronUp, Phone, Mail, Building2 } from "lucide-react";
import { fadeUp, Section, Card, GoldButton } from "../components/shared/UIComponents";
import OfferEstimator from "../components/landing/OfferEstimator";

const TRUST_ITEMS = ["Direct Buyer", "No Fees", "No Commissions", "48-Hour Offers", "Close in 21 Days", "Confidential"];

const WHY_SELL = [
  { icon: DollarSign, text: "No listing fees or commissions" },
  { icon: Clock, text: "We close in as little as 21 days" },
  { icon: Lock, text: "Confidential process — your tenants never know" },
  { icon: Shield, text: "We buy as-is — no repairs required" },
  { icon: CheckCircle, text: "We close on your timeline, not ours" },
];

const FAQ_DATA = [
  { q: "What types of properties do you buy?", a: "We acquire multifamily (5-500+ units), retail, industrial, mixed-use, and distressed properties. Our primary focus is multifamily and workforce housing." },
  { q: "How fast can you close?", a: "We can close in as little as 21 days. Most transactions close within 30-45 days. We work around your timeline." },
  { q: "Do I pay any fees or commissions?", a: "No. We are a direct buyer. There are no listing fees, broker commissions, or closing costs to you. If you have a broker, we are happy to work with them." },
  { q: "Do you buy properties as-is?", a: "Yes. We buy properties in any condition — occupied, vacant, deferred maintenance, code violations. No repairs needed before closing." },
  { q: "Is this process confidential?", a: "Absolutely. We respect your privacy and your tenants. No signs, no public listings, no disruption to your property operations." },
  { q: "What information do you need from me?", a: "Property type, address, number of units or square footage, occupancy, asking price (or we can make you an offer), and your preferred timeline. The more detail, the faster we can move." },
  { q: "What happens after I submit?", a: "We review your submission within 24 hours, schedule a brief call to learn more, and present a no-obligation written offer — typically within 48 hours of that call." },
];

const PROPERTY_TYPES = ["Multifamily (5+ units)", "Retail", "Industrial", "Mixed-Use", "Office", "Vacant / Land with Improvements", "Other"];
const TIMELINES = ["ASAP — As fast as possible", "30 days", "60 days", "90 days", "Flexible / No rush"];

export default function SellPage() {
  const [form, setForm] = useState({ property_type: "", address: "", units_sqft: "", occupancy: "", asking_price: "", make_offer: false, annual_noi: "", reason: "", timeline: "", name: "", email: "", phone: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sellers/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#EDE8DC] bg-white text-sm text-[#0A0A0A] placeholder-[#bbb] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 transition-colors";
  const labelCls = "block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5";

  return (
    <>
      {/* Hero */}
      <section className="relative py-16 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-4">Sell To Us</p>
            <h1 className="text-4xl font-bold text-cream sm:text-5xl md:text-6xl tracking-tight">Sell Your <span className="luxury-text-gradient">Commercial Property</span></h1>
            <p className="mt-5 max-w-2xl mx-auto text-base text-cream/70 leading-relaxed sm:text-lg">
              We are direct buyers of multifamily, retail, and industrial properties nationwide. No fees. No commissions. Offers within 48 hours.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="mt-8 flex flex-wrap justify-center gap-3">
            {TRUST_ITEMS.map((t, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full text-xs font-semibold text-gold/90 bg-emerald-900/50 border border-emerald-700/40 backdrop-blur-sm">{t}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Instant Offer Estimator */}
      <OfferEstimator />

      {/* Form */}
      <Section eyebrow="Property Details" title="Tell Us About Your Property"
        description="Fill out the form below and we'll review your submission within 24 hours.">
        {submitted ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">Submission Received</h3>
            <p className="text-base text-[#555] max-w-md mx-auto">Thank you for submitting your property. Our acquisitions team will review your submission and contact you within 24 hours.</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto" data-testid="sell-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Property Type *</label>
                <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })} required className={inputCls} data-testid="sell-property-type">
                  <option value="">Select property type</option>
                  {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Property Address *</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required placeholder="Full property address" className={inputCls} data-testid="sell-address" />
              </div>
              <div>
                <label className={labelCls}>Units or Square Footage</label>
                <input value={form.units_sqft} onChange={e => setForm({ ...form, units_sqft: e.target.value })} placeholder="e.g. 24 units or 15,000 sqft" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Occupancy %</label>
                <input value={form.occupancy} onChange={e => setForm({ ...form, occupancy: e.target.value })} placeholder="e.g. 85%" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Asking Price</label>
                <input value={form.asking_price} onChange={e => setForm({ ...form, asking_price: e.target.value })} placeholder="$ or 'Make me an offer'" className={inputCls} data-testid="sell-price" />
              </div>
              <div>
                <label className={labelCls}>Annual NOI</label>
                <input value={form.annual_noi} onChange={e => setForm({ ...form, annual_noi: e.target.value })} placeholder="Net Operating Income" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Reason for Selling</label>
                <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Retirement, 1031 exchange, partnership dissolution, etc." className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Timeline</label>
                <select value={form.timeline} onChange={e => setForm({ ...form, timeline: e.target.value })} className={inputCls}>
                  <option value="">Select preferred timeline</option>
                  {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 border-t border-[#EDE8DC] pt-5 mt-2">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#888] mb-4">Your Contact Information</p>
              </div>
              <div>
                <label className={labelCls}>Full Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Your name" className={inputCls} data-testid="sell-name" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(xxx) xxx-xxxx" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" className={inputCls} data-testid="sell-email" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Additional Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything else we should know about the property or your situation..." className={inputCls} />
              </div>
            </div>
            <div className="mt-8">
              <GoldButton type="submit" disabled={submitting} className="!w-full !py-4 !text-sm" data-testid="sell-submit-btn">
                {submitting ? "Submitting..." : "Submit Property"} {!submitting && <ArrowRight className="h-4 w-4 ml-1 inline" />}
              </GoldButton>
            </div>
            <p className="mt-3 text-xs text-center text-[#999]">Your information is confidential and will only be used by our acquisitions team.</p>
          </form>
        )}
      </Section>

      {/* What Happens Next */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">Our Process</p>
            <h2 className="mt-3 text-3xl font-bold text-cream sm:text-4xl">What Happens Next</h2>
          </motion.div>
          <div className="grid gap-6 lg:grid-cols-3 max-w-4xl mx-auto">
            {[
              { step: "1", title: "We Review", desc: "Our acquisitions team reviews your submission within 24 hours.", time: "24 hours" },
              { step: "2", title: "Brief Call", desc: "We schedule a brief call to learn more about your property and situation.", time: "48 hours" },
              { step: "3", title: "Written Offer", desc: "We present a no-obligation written offer. No strings attached.", time: "48-72 hours" },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 text-center">
                <span className="w-10 h-10 rounded-full bg-gold/20 text-gold text-lg font-bold flex items-center justify-center mx-auto mb-4">{s.step}</span>
                <h3 className="text-lg font-bold text-cream mb-2">{s.title}</h3>
                <p className="text-sm text-cream/70 leading-relaxed mb-3">{s.desc}</p>
                <span className="text-xs font-semibold text-gold/70">{s.time}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sell to Imaani */}
      <Section eyebrow="Why Us" title="Why Sell to Imaani Capital">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
          {WHY_SELL.map((w, i) => (
            <Card key={i} className="!p-5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                <w.icon className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="text-sm text-[#555] font-medium leading-relaxed">{w.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">FAQ</p>
            <h2 className="mt-3 text-3xl font-bold text-cream sm:text-4xl">Common Questions</h2>
          </motion.div>
          <div className="space-y-3">
            {FAQ_DATA.map((f, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.04 }}
                className="rounded-xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`faq-${i}`}>
                  <span className="text-sm font-semibold text-cream pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-gold shrink-0" /> : <ChevronDown className="h-4 w-4 text-gold/60 shrink-0" />}
                </button>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-5 pb-4">
                    <p className="text-sm text-cream/70 leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
